import {Box, Typography, TextField} from '@mui/material';
import {t} from 'i18next';
import {useEffect, useRef, useState, useId} from 'react';
import {useForm, Controller, SubmitHandler} from 'react-hook-form';
import AurClientDropdown from '../../client-management/aur-client-dropdown/AurClientDropdown';
import AurButton from '../../common/aur-button/AurButton';
import AurPopup from '../../common/aur-popup/AurPopup';
import {ReactComponent as InfoIcon} from '../../../../src/assests/svg/export_name_icon.svg';
import {RootState} from '../../../redux/store';
import {useSelector, useDispatch} from 'react-redux';
import {getExportDetails} from '../../../api/EcaManagementService';
import {
  setExportTab1Params,
  setSelectedExportId,
  setSelectedFieldsData,
} from '../../../redux/slices/addExportSlice';
import {useNavigate} from 'react-router-dom';
import {ReactComponent as TagFieldDisabledIcon} from '../../../../src/assests/svg/tag-value-icon.svg';
import {
  ID_SUFFIX,
  MAX_CHAR_LIMIT,
  POPUP_BUTTON_STYLE,
} from '../../../constants/constants';
import AurTooltip from '../../common/aur-tooltip/AurTooltip';
import {getMissingFamiliesDetails} from '../../../api/EcaServices';
import {AurLoadingButton} from '../../common/aur-loading-button/AurLoadingButton';
import AurToast from '../../common/aur-toast/AurToast';
import {setSearchLoader} from '../../../redux/slices/ecaAdhocSearchSlice';
import AurEcaSearchLoader from '../../common/aur-eca-search-loader/AurEcaSearchLoader';
import AurSvgIcon from '../../common/aur-svg-icon/AurSvgIcon';
import {
  ExportFileTypeData,
  ExportNameData,
  ExportNamesData,
} from './CreateExportTypes';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';

interface ExportFormsProps {
  radioBtnType: string;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  tagsData: ITags[];
  exportNamesList: ExportNameData[];
  activeStep: number;
  fieldTempId: number | string;
  exportFileType: ExportFileTypeData[];
  groupIdentifierId: any;
}

// Define the type for form data
interface ITags {
  id: string;
  label: string;
  name: string;
  values: any;
}
interface FormData {
  exportName: string;
  notes: string;
  client: string;
}
interface IRunSearchError {
  message: string;
  display: boolean;
}

const ExportForms: React.FC<ExportFormsProps> = ({
  radioBtnType,
  setActiveStep,
  tagsData,
  exportNamesList,
  exportFileType,
  groupIdentifierId,
}) => {
  const {
    handleSubmit,
    control,
    getValues,
    reset,
    setValue,
    trigger,
    formState: {errors, isValid},
  } = useForm<FormData>({
    mode: 'onChange', // to trigger validation on each change
    defaultValues: {
      exportName: '',
      notes: '',
    },
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {trackGAEvent} = useAnalytics();
  const [showCancelPopup, setShowCancelPopup] = useState<boolean>(false);
  const [pickTagValue, setPickTagValue] = useState<any>(undefined);
  const [pickedTagFieldValue, setPickedTagFieldValue] =
    useState<any>(undefined);
  const [tagDropDown, setTagDropDown] = useState<any[]>([]);

  const [tagFieldDisabled, setTagFieldDisabled] = useState<string>('Y');

  const [pickedExportName, setPickedExportName] = useState<
    ExportNamesData | undefined
  >(undefined);
  const [pickExportFileType, setPickExportFileType] =
    useState<ExportFileTypeData>({id: '1', name: 'DAT'});
  const [showDocsPopup, setShowDocsPopup] = useState<boolean>(false);

  const [runSearchError, setRunSearchError] = useState<IRunSearchError>({
    message: '',
    display: false,
  });

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const onSubmit: SubmitHandler<FormData> = () => {};

  //  Refs for next btn api calls manage everything
  const sseRefs = useRef<EventSource[]>([]);
  const controllersRef = useRef<{[key: number]: AbortController}>({});
  const isCancelledRef = useRef(false);

  const tagFieldDropDown = tagsData;

  const onTagFieldPick = (id: string) => {
    const tag_field_value: ITags | undefined = tagFieldDropDown.find(
      (item) => item.id === id
    );
    setTagFieldDisabled('N');
    setPickedTagFieldValue(tag_field_value);
    setTagDropDown(tag_field_value?.values);
  };

  const getExportTab1 = useSelector(
    (state: RootState) => state.addExport?.exportTabsValues
  );
  const fetchExportNameId = useSelector(
    (state: RootState) => state.addExport?.selectedExportId
  );

  const {clientId, projectId} = useSelector(
    (state: RootState) => state?.reports
  );

  const step2SelectedField = useSelector(
    (state: RootState) => state.addExport?.selectedFields
  );

  const [popupData, setPopupData] = useState<{
    iconSrc: string;
    heading: string;
    description: string;
    type: 'member_missing' | 'duplicate_member' | 'missing_group_identifier';
    continueText: string;
  } | null>(null);

  const saveHintId = useId();

  useEffect(() => {
    /* istanbul ignore next */
    if (Object.keys(getExportTab1)?.length > 0) {
      setTimeout(() => {
        setValue('exportName', getExportTab1.name);
        if (getExportTab1.notes) setValue('notes', getExportTab1.notes);
        setPickedTagFieldValue({
          id: getExportTab1.tagFieldId,
          label: getExportTab1.tagFieldLabel,
          name: getExportTab1.tagFieldName,
        });
        const matchedFieldObject: any = tagFieldDropDown.find(
          (item) => item.id == getExportTab1.tagFieldId
        );
        if (matchedFieldObject?.values) {
          setTagDropDown(matchedFieldObject?.values);
        }

        setTagFieldDisabled('N');
        if (radioBtnType == 'export_for_analytics') {
          if (getExportTab1.exportFileType) {
            if (getExportTab1.exportFileType == 'DAT') {
              setPickExportFileType({id: '1', name: 'DAT'});
            } else {
              setPickExportFileType({id: '2', name: 'CSV'});
            }
          }
          if (fetchExportNameId) {
            setPickedExportName({exportId: fetchExportNameId});
            const fetchFieldLabel = step2SelectedField.map(
              (value: any) => value.fieldLabel
            );
            dispatch(setSelectedFieldsData(fetchFieldLabel));
          }
        }
      }, 300);
      setTimeout(() => {
        if (getExportTab1.tagValueId)
          setPickTagValue({
            id: getExportTab1.tagValueId,
            value: getExportTab1.tagValueLabel,
          });
        trigger();
      }, 400);
    }
  }, [setActiveStep]);

  const onTagPick = (id: string) => {
    const tag_value = tagDropDown?.find((item) => item.id === id);
    setPickTagValue(tag_value);
  };

  useEffect(() => {
    if (runSearchError.display) {
      setTimeout(() => {
        setRunSearchError({
          message: '',
          display: false,
        });
      }, 4000);
    }
  }, [runSearchError]);

  const {searchLoader} = useSelector(
    (state: RootState) => state?.ecaAdhocSearch
  );

  useEffect(() => {
    dispatch(setSearchLoader(false));
  }, []);

  const saveRunBtnHandler = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.next'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.TAB,
        action: t('gaTracking.export.newExportGeneralInfoEntered'),
        details:
          radioBtnType !== 'export_for_analytics'
            ? t('gaTracking.export.exportTypeReview')
            : t('gaTracking.export.exportTypeAnalyticsCustom', {
                isCustom: pickedExportName?.exportId ? 'YES' : 'NO',
              }),
      },
      subPage: {
        subpage_name: t('gaTracking.export.generalInfo'),
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
    if (groupIdentifierId) {
      const savedValues = {
        name: getValues('exportName'),
        notes: getValues('notes'),
        tagFieldId: pickedTagFieldValue.id,
        tagFieldName: pickedTagFieldValue.name,
        tagFieldLabel: pickedTagFieldValue.label,
        tagValueId: pickTagValue.id,
        tagValueLabel: pickTagValue.value,
        tagValueName: pickTagValue.value,
      };

      dispatch(setSelectedExportId(pickedExportName?.exportId ?? ''));
      if (radioBtnType === 'export_for_analytics') {
        dispatch(
          setExportTab1Params({
            ...savedValues,
            ...{exportFileType: pickExportFileType.name},
          })
        );

        setActiveStep((prev: number) => prev + 1);
      } else {
        dispatch(setExportTab1Params(savedValues));
        checkFamilyMemberMissingFlag();
      }
    } else {
      setPopupData({
        iconSrc: '../assests/images/export_notification.svg',
        heading: t('eca.newExport.exportReview'),
        description: t('eca.newExport.groupIdentifierDialogTitle'),
        type: 'missing_group_identifier',
        continueText: t('eca.newExport.okButtonLabel'),
      });
      setShowDocsPopup(true);
    }
  };

  const checkFamilyMemberMissingFlag = async () => {
    const params = `?clientId=${clientId}&projectId=${projectId}&tagvalueId=${pickTagValue.id}&relationshipgroupIds=${groupIdentifierId}`;
    try {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
        subPage: {
          subpage_name: t('gaTracking.export.crossCheckingDocs'),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
      });
      dispatch(setSearchLoader(true));
      const results = await getMissingFamiliesDetails(params);
      const response = results?.data.data;
      if (response['hasMissingFamilyTagDocuments']) {
        setPopupData({
          iconSrc: '../assests/images/export_warning_icon.svg',
          heading: t('eca.newExport.familyMemberMissing'),
          description: t('eca.newExport.returnBackDocList'),
          type: 'member_missing',
          continueText: t('eca.newExport.viewTagDocList'),
        });
        dispatch(setSearchLoader(false));
        setShowDocsPopup(true);
      } else if (response['isDuplicateDocs']) {
        setPopupData({
          iconSrc: '../assests/images/export_notification.svg',
          heading: t('eca.newExport.duplicateContent'),
          description: t('eca.newExport.addDocsContent'),
          type: 'duplicate_member',
          continueText: t('buttons.yesContinue'),
        });
        dispatch(setSearchLoader(false));
        setShowDocsPopup(true);
      } else {
        setActiveStep((prev: number) => prev + 1);
      }
    } catch (error: any) {
      dispatch(setSearchLoader(false));
      const data = error?.response?.data;
      if (data?.meta?.message) {
        setRunSearchError({
          message: data.meta.message,
          display: true,
        });
      } else {
        setRunSearchError({
          message: t('common.somethingWrong'),
          display: true,
        });
      }
    }
  };

  const handledocsConfirm = () => {
    if (popupData?.type === 'member_missing') {
      const parentIndex = tagsData.findIndex(
        (item) => item.id === pickedTagFieldValue?.id
      );
      const tagField = tagsData[parentIndex];
      const childIndex = tagField.values.findIndex(
        (item: any) => item.id === pickTagValue?.id
      );
      const calcIndex = tagField.values.length - 1 - childIndex;
      const selectedTag = {
        id: `${pickTagValue?.id}${ID_SUFFIX.CHILD_TAG}${pickedTagFieldValue?.id}${calcIndex}`,
        label: pickTagValue.value,
        fieldId: `${pickedTagFieldValue?.id}${ID_SUFFIX.PARENT_TAG}${parentIndex}`,
        parentFolder: pickedTagFieldValue.label,
        parentTagName: pickTagValue.value,
        categoryId: [groupIdentifierId],
      };
      navigate('/index-search', {
        state: {
          source: 'tag',
          selected: selectedTag,
        },
      });
    } else if (popupData?.type === 'duplicate_member') {
      setActiveStep((prev: number) => prev + 1);
    }
    setShowDocsPopup(false);
  };

  const cancelBtnHandler = () => {
    setShowCancelPopup(true);
  };

  const cancelModalHandler = () => {
    setShowCancelPopup(false);
  };

  useEffect(() => {
    if (radioBtnType) setTagFieldDisabled('Y');
    setPickedTagFieldValue({id: null});
    setPickTagValue({id: null});
    setPickedExportName({exportId: null});
    dispatch(setSelectedFieldsData([]));
    reset();
    if (radioBtnType === 'export_for_analytics') {
      setPickExportFileType({id: '1', name: 'DAT'});
    }
  }, [radioBtnType]);

  const handleContinue = () => {
    navigate('/eci-home-page/export-management');
  };

  const onExportNamedPick = (exportId: string) => {
    const exportNames = exportNamesList.find(
      (item) => item.exportId === exportId
    );
    if (exportNames) {
      setPickedExportName(exportNames); // Set selected export name
      exportDetails(exportNames.exportId);
    }
  };

  const exportDetails = async (exportId: string) => {
    const params = `${exportId}?clientId=${clientId}&projectId=${projectId}`;
    try {
      const results = await getExportDetails(params);
      const exportDatas = results?.data?.data;
      if (radioBtnType === 'export_for_analytics') {
        setValue('exportName', exportDatas.exportName);
        trigger('exportName');
        setPickedTagFieldValue({id: exportDatas.tagFieldId.toString()});
        if (exportDatas.exportCustomFields.length > 0) {
          const fetchFieldLabel = exportDatas.exportCustomFields.map(
            (value: any) => value.fieldLabel
          );
          dispatch(setSelectedFieldsData(fetchFieldLabel));
        }
        const matchedFieldObject = tagFieldDropDown.find(
          (item) => item.id == exportDatas.tagFieldId
        );
        if (matchedFieldObject) {
          setPickedTagFieldValue({
            id: exportDatas.tagFieldId,
            label: matchedFieldObject.label,
            name: matchedFieldObject.name,
          });
          setTagDropDown(matchedFieldObject.values);
          setTagFieldDisabled('N');
          const matchedFieldTagObject = matchedFieldObject.values.find(
            (item: any) => item.id === exportDatas.tagValueId
          );
          setPickTagValue({
            id: matchedFieldTagObject.id,
            value: matchedFieldTagObject.value,
          });
        } else {
          setPickedTagFieldValue({id: null});
          setPickTagValue({id: null});
        }
      }
    } catch (e) {
      console.log(e);
    }
  };

  const closeDocsModel = () => {
    setShowDocsPopup(false);
  };

  const onExportFilePick = (id: string) => {
    const exportFile = exportFileType.find((item) => item.id === id);
    setPickExportFileType(exportFile!);
  };

  const handleSnackbarClose = () => {
    setRunSearchError({
      message: '',
      display: false,
    });
  };

  //  Cancel handler
  const handleLoaderClose = (value: string | undefined) => {
    if (value === 'cancel') {
      dispatch(setSearchLoader(false));
      isCancelledRef.current = true;

      // Cancel all in-flight APIs
      Object.values(controllersRef.current).forEach((controller) =>
        controller?.abort?.()
      );

      // Close SSEs
      sseRefs.current.forEach((sse) => sse.close?.());
      sseRefs.current = [];

      // Optional: Clear state if needed
      controllersRef.current = {};
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: t('gaTracking.export.cancelThisSearch'),
          type: GA_CLICK_TYPES.BUTTON,
          region: GA_REGIONS.MODAL,
          action: t('gaTracking.export.cancelledSearch'),
        },
        subPage: {
          subpage_name: t('gaTracking.export.crossCheckingDocs'),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
      });
    }
  };

  // Watch for form changes to enable/disable submit button
  const isFormValid =
    isValid &&
    !errors.exportName &&
    pickedTagFieldValue?.id &&
    pickTagValue?.id;

  return (
    <Box className="export-form">
      <form
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        className="form-container"
      >
        {radioBtnType === 'export_for_analytics' && (
          <Box className="analytics-export-name">
            <div className="export-label">
              <Typography
                className="typo-text subhead4_size"
                style={{paddingBottom: '10px'}}
              >
                {t('eca.newExport.existingExportLabel')}
              </Typography>
              <AurTooltip
                className="typo-text label3_size"
                title={t(`indexes.viewSettings.analyticsInfoTitle`)}
                tabIndex={0}
              >
                <InfoIcon />
              </AurTooltip>
            </div>
            <Typography className="typo-text body3_size export-desc">
              {t('eca.newExport.existingExportDesc')}
            </Typography>
            <AurClientDropdown
              buttonOrTextField={'text'}
              data={exportNamesList}
              buttonLabel={t('eca.export.button.selectExport')}
              dropDownID="exportfields"
              idKey="exportId"
              displayKey="exportName"
              selectDefaultFirstItem={false}
              initialSelectedOptionId={pickedExportName?.exportId ?? undefined}
              onOptionPick={onExportNamedPick}
              buttonBorderHighlight={true}
              enableSearch={true}
              ariaLabel={t('eca.export.button.selectExistingExport')}
            />
          </Box>
        )}
        <div className="fieldsets field-1">
          <Typography
            className="typo-text subhead4_size"
            style={{paddingBottom: '10px'}}
          >
            {t('eca.newExport.nameExport')}
          </Typography>
          <Controller
            name="exportName"
            control={control}
            rules={{
              required: t('eca.newExport.exportRequired'),
              pattern: {
                // value: /^(?!.*\s{2,})[A-Za-z0-9 _-]+(\s[A-Za-z0-9 _-]+)*$/, // Only letters, numbers,space, hyphens, and underscores
                value: /^[A-Za-z0-9_-]+$/,
                message: t('eca.newExport.CheckSpecChar'),
              },
              maxLength: {
                value: 50,
                message: t('eca.newExport.maxChar50'),
              },
            }}
            render={({field}) => (
              <TextField
                {...field}
                name={t('eca.newExport.exportName')}
                label={t('eca.newExport.exportName')}
                variant="filled"
                className="team-member-info-field"
                fullWidth
                data-testid="exportField"
                helperText={
                  errors.exportName?.message ||
                  t('eca.newExport.charSpecChar50')
                }
                placeholder={t('eca.newExport.exportNamePlaceholder')}
                error={!!errors.exportName}
                inputProps={{maxLength: MAX_CHAR_LIMIT}}
                InputProps={{
                  className: errors?.exportName?.message && 'inputError',
                }}
                InputLabelProps={{
                  className: errors?.exportName?.message && 'label-error',
                }}
                FormHelperTextProps={{
                  'aria-live': 'polite',
                  'aria-description': errors?.exportName?.message
                    ? t('eca.newExport.errorHint')
                    : '',
                  'aria-invalid': !!errors?.exportName?.message,
                }}
                style={{marginBottom: '16px'}}
              />
            )}
          />
        </div>
        {/* Export Name Field */}

        {/* Notes Field */}
        <div className="fieldsets field-2">
          <Typography
            className="typo-text subhead4_size"
            style={{paddingBottom: '10px'}}
          >
            {t('eca.newExport.notes')}
          </Typography>
          <Controller
            name="notes"
            control={control}
            rules={{
              maxLength: {
                value: 500,
                message: t('eca.newExport.noteslength500'),
              },
            }}
            render={({field}) => (
              <TextField
                {...field}
                name={t('eca.newExport.notes')}
                label={t('eca.newExport.notes')}
                variant="filled"
                fullWidth
                className="team-member-info-field"
                helperText={
                  errors.notes?.message || t('eca.newExport.notesHelper')
                }
                error={!!errors.notes}
                inputProps={{maxLength: 500}}
                multiline
                maxRows={100}
                placeholder={t('eca.newExport.notesPlaceholder')}
                InputProps={{
                  className: errors?.notes?.message && 'inputError',
                }}
                InputLabelProps={{
                  className: errors?.notes?.message && 'label-error',
                }}
                style={{marginBottom: '16px'}}
              />
            )}
          />
        </div>

        <Box
          className={`selectField ${radioBtnType === 'export_for_review' ? 'review-tag-dropdown' : 'analy-tag-dropdown'}`}
        >
          <Typography
            className="typo-text subhead4_size"
            style={{paddingBottom: '10px'}}
          >
            {t('eca.newExport.selectType')}
          </Typography>
          <div className="select-field-row">
            <div style={{width: '332px'}}>
              <AurClientDropdown
                buttonOrTextField="button"
                dataTestId="tag-field-dropdown"
                data={tagFieldDropDown}
                buttonLabel={t('eca.newExport.tagField')}
                dropDownID="TagFields"
                idKey="id"
                displayKey="label"
                selectDefaultFirstItem={false}
                initialSelectedOptionId={
                  pickedTagFieldValue?.id ? pickedTagFieldValue?.id : null
                }
                onOptionPick={onTagFieldPick}
                buttonBorderHighlight={true}
                enableSearch={true}
                ariaLabel={t('eca.newExport.tagField')}
                ariaDescription={t(
                  'accessibility.indexManagement.create.listHint'
                )}
              />
            </div>

            <div style={{width: '332px', position: 'relative'}}>
              {tagFieldDisabled == 'Y' && (
                <div className="tag-field-disabled-icon">
                  <TagFieldDisabledIcon aria-hidden="true" />
                </div>
              )}
              <AurClientDropdown
                buttonOrTextField="text"
                dataTestId="tag-value-dropdown"
                data={tagDropDown}
                buttonLabel={t('eca.newExport.tag')}
                dropDownID="Project1"
                idKey="id"
                displayKey="value"
                selectDefaultFirstItem={false}
                initialSelectedOptionId={
                  pickTagValue?.id ? pickTagValue?.id : null
                }
                disableDropDownForSingleItem={false}
                onOptionPick={onTagPick}
                disableDropDown={tagFieldDisabled}
                buttonBorderHighlight={true}
                enableSearch={true}
                ariaLabel={t('eca.newExport.tag')}
                ariaDescription={
                  tagFieldDisabled
                    ? t('eca.newExport.tagHint')
                    : t('accessibility.indexManagement.create.listHint')
                }
              />
            </div>
          </div>
        </Box>

        {radioBtnType === 'export_for_analytics' && (
          <Box
            className="analytics-export-name export-file-type"
            style={{marginTop: '30px'}}
          >
            <Typography
              className="typo-text subhead4_size export-label"
              style={{paddingBottom: '10px'}}
            >
              {t('eca.newExport.exportFileType')}
            </Typography>
            <Typography className="typo-text body3_size export-desc">
              {t('eca.newExport.chooseExportFile')}
            </Typography>
            <AurClientDropdown
              buttonOrTextField="button"
              data={exportFileType}
              buttonLabel={t('eca.newExport.fileType')}
              dropDownID="exportFileTypes"
              idKey="id"
              displayKey="name"
              selectDefaultFirstItem={false}
              initialSelectedOptionId={
                pickExportFileType?.id ? pickExportFileType?.id : null!
              }
              onOptionPick={onExportFilePick}
              buttonBorderHighlight={true}
              enableSearch={true}
              ariaLabel={t('eca.newExport.fileType')}
            />
          </Box>
        )}
      </form>
      <Box
        className="export-action-container"
        style={{
          marginTop:
            radioBtnType === 'export_for_review' || !radioBtnType
              ? '350px'
              : '0px',
        }}
      >
        <div className="custom-index-action-button">
          <AurButton
            type="exit"
            label={t('buttons.cancel')}
            title={t('buttons.cancel')}
            size="Medium"
            onClick={() => {
              cancelBtnHandler();
            }}
            classNames="index-cancel-btn"
            ariaDescription={t('eca.newExport.cancelHint')}
          />
          <AurLoadingButton
            name={t('buttons.next')}
            label={t('buttons.next')}
            loading={false}
            size="Medium"
            onClick={() => {
              saveRunBtnHandler();
            }}
            classNames="export-next-btn"
            disabled={!isFormValid}
            ariaDescribedBy={saveHintId}
          />
          <span id={saveHintId} className="sr-only">
            {t('eca.newExport.saveHint')}
          </span>
        </div>
      </Box>
      {showCancelPopup && (
        <div>
          <AurPopup
            className="aur-unsaved-changes-popup export-cancel-popup"
            open={showCancelPopup}
            onOk={cancelModalHandler}
            onCancel={handleContinue}
            popupHeading={t('eca.newExport.expoCancelPopupHeading')}
            showPopupHeading={true}
            popupTitle={t('common.clearAllPopup.heading')}
            showPopupTitle={true}
            showCloseIcon={false}
            popupContent={t('eca.newExport.expoCancelPopupContent')}
            labelActionCancel={t('eca.newExport.discardChangeBtn')}
            labelActionOk={t('eca.newExport.stayOnPageBtn')}
            exitButtonHint={t('common.clearAllPopup.cacnelToClearHint')}
            continueButtonHint={t('common.clearAllPopup.confirmToClearHint')}
            cancelButtonTitle=""
          />
        </div>
      )}

      <div>
        <AurPopup
          className={`aur-unsaved-changes-popup add-export-submit-popup ${popupData?.type == 'member_missing' ? 'popup-btn-class' : ''}`}
          open={showDocsPopup}
          onOk={handledocsConfirm}
          onCancel={closeDocsModel}
          popupHeading={popupData?.heading}
          showPopupHeading={true}
          headingIcon={<AurSvgIcon src={popupData?.iconSrc ?? ''} ariaHidden />}
          showPopupTitle={false}
          showCloseIcon={false}
          popupContent={popupData?.description}
          labelActionCancel={t('common.clearAllPopup.buttonCancel')}
          labelActionOk={popupData?.continueText}
          exitButtonHint={t('common.clearAllPopup.cacnelToClearHint')}
          continueButtonHint={popupData?.continueText}
          dataTestId="aur-popup"
          showCancelOption={popupData?.type !== 'missing_group_identifier'}
          buttonStyle={
            popupData?.type === 'missing_group_identifier'
              ? POPUP_BUTTON_STYLE.STACKED
              : POPUP_BUTTON_STYLE.SIDEBYSIDE
          }
        />
      </div>

      <AurToast
        ariaLabel={t('common.toastAlert.toastAlertHint')}
        hideDuration={5000}
        open={runSearchError.display}
        severity={'error'}
        horizontalPosition={'center'}
        verticalPosition={'bottom'}
        message={runSearchError.message}
        onClose={() => handleSnackbarClose}
      />

      {searchLoader && (
        <AurEcaSearchLoader
          isOpen={searchLoader}
          onClose={handleLoaderClose}
          isExportDoc={false}
          isExportForm={true}
          keepMounted
        />
      )}
    </Box>
  );
};

export default ExportForms;
